import { Component, OnInit, Input } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import * as _ from 'lodash';
import { GeoService } from 'src/app/planner/services/geo.service';
import { TreeService } from './../../../../common/components/checkbox-tree/tree.service';
import { RegionMapTypes } from 'src/app/planner/dto/GeoTargetingDTO';
import { RegionMapVO } from 'src/app/planner/interfaces/vo/RegionMapVO';
import { RegionVO } from 'src/app/planner/interfaces/RegionVO';
import { RegionValueVO } from 'src/app/planner/interfaces/RegionValueVO';
import { RegionValuesVO } from 'src/app/planner/interfaces/vo/RegionValuesVO';
import { IButtonProps, ButtonVariant } from '@amobee/component-library';

@Component({
  selector: 'ccp-geo-targeting',
  templateUrl: './geo-targeting.component.html',
  styleUrls: ['./geo-targeting.component.scss'],
  providers: [
    TreeService,
    GeoService
  ]
})
export class GeoTargetingComponent implements OnInit {

  @Input() group: any;
  @Input() formArray: FormArray;

  open: boolean = false;
  selectedRegions: any = [];
  continents: any;
  selectedRegionsInSideBar: any = [];
  continentsInSideBar: any;
  loading: boolean;
  targetBy: RegionMapTypes = RegionMapTypes.GEOPEDIA;
  regionMap: RegionMapVO;

  get regionMapType(): RegionMapTypes {
    return RegionMapTypes.GEOPEDIA;
  }

  private responseData: any;
  private voSelectedRegions: any;
  private geographicLevels: any = [
    'continent',
    'country',
    'region',
    'subregion',
    'cityOrDMA'
  ];
  saveButton: IButtonProps = {
    variant: ButtonVariant.PRIMARY,
    label: 'Save',
    onClick: () => {
      this.saveGeoTargeting();
    }
  };
  cancelButton: IButtonProps = {
    variant: ButtonVariant.NEUTRAL,
    label: 'Cancel',
    onClick: () => {
      this.cancel();
    }
  };

  constructor(
    private treeService: TreeService,
    private geoService: GeoService
  ) { }

  ngOnInit() {
    if (this.geoTargetingControl.disabled) {
      this.saveButton.disabled = true;
    }
    this.loading = true;
    this.regionMap = this.geoTargetingControl.value;
    this.initializeByType(this.regionMapType);
  }

  private initializeByType(type: RegionMapTypes): void {
    switch (type) {
      case RegionMapTypes.GEOPEDIA:
        this.initGeopedia();
        break;
    }
  }

  private async initGeopedia(): Promise<void> {
    this.loading = true;
    this.continents = await this.geoService.getContinents();
    this.treeService.extendData(this.continents, { level: 'continent' });

    const targetedRegions = this.regionMap.targetedRegions; //[];
    const regionMap = this.regionMap;

    // If there are values coming back from the API, populate them.
    if (targetedRegions && targetedRegions.length) {
      this.voSelectedRegions = this.convertTargetedRegionsToSelectedRegions(
        targetedRegions
      );
      const selection = await this.treeService.loadSelection(
        this.continents,
        this.geographicLevels,
        this.voSelectedRegions,
        this.expand
      );
      this.continents = selection;

      // Once values are populated, refresh the tree
      this.treeService.reloadAttrs(selection);
      this.changed();
    } else {
      this.voSelectedRegions = this.convertTargetedRegionsToSelectedRegions(
        (regionMap && regionMap.targetedRegions) || []
      );

      const selection = await this.treeService.loadSelection(
        this.continents,
        this.geographicLevels,
        this.voSelectedRegions,
        this.expand
      );
      this.continents = selection;

      this.treeService.reloadAttrs(selection);
      this.changed();
    }

    this.loading = false;
  }

  public convertTargetedRegionsToSelectedRegions(targetedRegions): any {
    const selectedRegions = [];
    targetedRegions.forEach(targetedItem => {
      if (
        targetedItem &&
        targetedItem.fullyQualifiedValue &&
        typeof targetedItem.fullyQualifiedValue === 'object'
      ) {
        const selectedItem = {
          id: '',
          subregion: '',
          cityOrDMA: ''
        };

        selectedItem.id = targetedItem.id;
        let key;
        for (key in targetedItem.fullyQualifiedValue) {
          if (
            targetedItem.fullyQualifiedValue.hasOwnProperty(key) &&
            key !== 'city' &&
            key !== 'dma'
          ) {
            if (targetedItem.fullyQualifiedValue[key]) {
              selectedItem[key] =
                targetedItem.fullyQualifiedValue[key];
            }
          }
        }

        // add subregion level for Cities and DMAs
        if (targetedItem.type === 'City') {
          selectedItem.subregion =
            targetedItem.fullyQualifiedValue.region + '|Cities';
          selectedItem.cityOrDMA =
            targetedItem.fullyQualifiedValue.city;
        }

        if (targetedItem.type === 'Dma') {
          selectedItem.subregion =
            targetedItem.fullyQualifiedValue.region + '|DMAs';
          selectedItem.cityOrDMA =
            targetedItem.fullyQualifiedValue.dma;
        }

        selectedRegions.push(selectedItem);
      }
    });

    return selectedRegions;
  }

  private nextLevel(level): any {
    return this.geographicLevels[this.geographicLevels.indexOf(level) + 1];
  }

  private prepareTree(
    response,
    item,
    childrenLevel,
    callback,
    resolver
  ): void {
    this.treeService.extendData(response, {
      level: childrenLevel,
      parent: item
    });

    if (childrenLevel === 'region') {
      response.forEach(region => {
        region.hasChildren = region.hasCities || region.hasDMAs;
      });
    }

    if (response && response.length) {
      item.children = response;
    } else {
      item.hasChildren = false;
    }

    this.treeService.change(item, item.checked);

    if (callback) {
      callback(item.children);
    }
    resolver();
  }

  private isCityOrDMA(item): any {
    if (!item || !item.level) {
      return null;
    }

    if (item.level !== 'subregion' && item.level !== 'cityOrDMA') {
      return null;
    }

    const subregionSplitedValue =
      item.level === 'subregion'
        ? item.value.split('|')
        : item.getParent().value.split('|');

    if (
      subregionSplitedValue &&
      subregionSplitedValue.length &&
      subregionSplitedValue.length > 1
    ) {
      if (
        subregionSplitedValue[subregionSplitedValue.length - 1] ===
        'Cities'
      ) {
        return 'city';
      }

      if (
        subregionSplitedValue[subregionSplitedValue.length - 1] ===
        'DMAs'
      ) {
        return 'dma';
      }
    }

    return null;
  }

  public changed(): void {
    if (!this.open) {
      this.selectedRegions = this.treeService.getSelected(this.continents);
      this.selectedRegions.forEach(item => {
        if (item && item.level && item.level === 'subregion') {
          this.expand(item);
        }
      });
      if (!this.loading) {
        this.saveGeoTargeting();
      }
    } else {
      this.selectedRegionsInSideBar = this.treeService.getSelected(this.continentsInSideBar);
      this.selectedRegionsInSideBar.forEach(item => {
        if (item && item.level && item.level === 'subregion') {
          this.expand(item);
        }
      });
    }
  }

  public removeItem(item): void {
    this.treeService.change(item, false);
    if (!this.open) {
      this.treeService.reloadAttrs(this.continents);
    } else {
      this.treeService.reloadAttrs(this.continentsInSideBar);
    }
    this.changed();
  }

  private getChildrenEndpointUrl(item): string {
    switch (item.level) {
      case 'continent':
        return 'countries/' + item.id;

      case 'country':
        return 'regions/' + item.id;

      case 'subregion':
        const type = this.isCityOrDMA(item);
        if (type && type === 'city') {
          return 'cities/' + item.getParent().id;
        }
        if (type && type === 'dma') {
          return 'dmas/' + item.getParent().id;
        }
    }
    return null;
  }

  public expand = (item, callback?): Promise<any> => {
    const promise = new Promise((resolve, reject) => {
      if (!item.children && item.hasChildren) {
        const childrenLevel = this.nextLevel(item.level);
        if (childrenLevel) {
          if (childrenLevel === 'subregion') {
            const subregionData = [];

            if (item.hasCities) {
              subregionData.push({
                id: item.id + '|0',
                label: 'Cities',
                value: item.value + '|Cities',
                hasChildren: true
              });
            }
            if (item.hasDMAs) {
              subregionData.push({
                id: item.id + '|1',
                label: 'DMAs',
                value: item.value + '|DMAs',
                hasChildren: true
              });
            }

            this.treeService.extendData(subregionData, {
              level: childrenLevel,
              parent: item
            });

            item.children = subregionData;
            this.treeService.change(item, item.checked);

            if (callback) {
              callback(item.children);
            }
            resolve();
          } else {
            const url = this.getChildrenEndpointUrl(item);

            if (url) {
              this.geoService.getChildren(url).then(response => {
                this.responseData = [...response];
                this.prepareTree(
                  response,
                  item,
                  childrenLevel,
                  callback,
                  resolve
                );
              });
            } else {
              this.prepareTree(
                this.responseData,
                item,
                childrenLevel,
                callback,
                resolve
              );
            }
          }
        }
      } else {
        if (callback) {
          callback(item.children);
        }
        resolve();
      }
    });
    return promise;
  }

  public getFullyQualifiedName(item): string {
    const out = [];

    if (item) {
      const parent = this.treeService.getParents(item).reverse();

      parent.forEach(branch => {
        out.push(branch.label ? branch.label : branch.value);
      });
      out.push(item.label ? item.label : item.value);

      return out.join(' > ');
    }

    return '';
  }

  public getFullyQualifiedValue(item): any {
    const type = this.isCityOrDMA(item);

    const regionValue: RegionValueVO = {
      continent:
        item.level === 'continent'
          ? item.value
          : item.continent || null,
      country:
        item.level === 'country' ? item.value : item.country || null,
      region: item.level === 'region' ? item.value : item.region || null,
      city:
        item.level === 'cityOrDMA' && type && type === 'city'
          ? item.value
          : null,
      dma:
        item.level === 'cityOrDMA' && type && type === 'dma'
          ? item.value
          : null,
      subregion: null
    };

    if (item.level === 'subregion') {
      regionValue.subregion = item.value;
    }

    return regionValue;
  }

  public getItemName = function (item) {
    if (item.level === 'subregion') {
      return item.getParent().label + ' (' + item.label + ')';
    }
    return item.label;
  }

  removeAllItem(selectedRegion: Array<any>) {
    _.forEach(selectedRegion, (item) => {
      this.removeItem(item);
    });
  }

  public convertSelectedRegionsToRegionValues(selectedRegions): any {
    const regionValues: RegionValuesVO = {
      continents: [],
      countries: [],
      regions: [],
      subregions: [],
      cities: [],
      dmas: []
    };

    _.forEach(selectedRegions, selectedItem => {
      switch (selectedItem.level) {
        case 'continent':
          regionValues.continents.push(selectedItem.value);
          break;

        case 'country':
          regionValues.countries.push(selectedItem.value);
          break;

        case 'region':
          regionValues.regions.push(selectedItem.value);
          break;

        case 'subregion':
          if (selectedItem.children) {
            const subregionType = this.isCityOrDMA(selectedItem);

            if (subregionType && subregionType === 'city') {
              selectedItem.children.forEach(item => {
                regionValues.cities.push(item.value);
              });
            }

            if (subregionType && subregionType === 'dma') {
              selectedItem.children.forEach(item => {
                regionValues.dmas.push(item.value);
              });
            }
          }
          break;

        case 'cityOrDMA':
          const type = this.isCityOrDMA(selectedItem);

          if (type && type === 'city') {
            regionValues.cities.push(selectedItem.value);
          }

          if (type && type === 'dma') {
            regionValues.dmas.push(selectedItem.value);
          }
          break;
      }
    });

    return regionValues;
  }

  private getRegionType(item): string {
    switch (item.level) {
      case 'continent':
        return 'Continent';

      case 'country':
        return 'Country';

      case 'region':
        return 'Region';

      case 'subregion':
        return 'Subregion';

      case 'cityOrDMA':
        const type = this.isCityOrDMA(item);
        if (type && type === 'city') {
          return 'City';
        }
        if (type && type === 'dma') {
          return 'Dma';
        }
    }
  }

  public convertSelectedRegionsToTargetedRegions(selectedRegions): any {
    const targetedRegions = [];

    selectedRegions.forEach(selectedItem => {
      const targetedItem: RegionVO = {
        id: selectedItem.id,
        type: this.getRegionType(selectedItem),
        name: selectedItem.label,
        fullyQualifiedValue: this.getFullyQualifiedValue(selectedItem),
        fullyQualifiedName: this.getFullyQualifiedName(selectedItem)
      };

      targetedRegions.push(targetedItem);
    });

    return targetedRegions;
  }

  openSidebar() {
    if (!this.isDisabled) {
      this.selectedRegionsInSideBar = _.cloneDeep(this.selectedRegions);
      this.continentsInSideBar = _.cloneDeep(this.continents);
      this.open = true;
    }
  }

  cancel() {
    this.open = false;
  }

  saveGeoTargeting() {
    // Save Data
    if (this.open) {
      this.continents = _.cloneDeep(this.continentsInSideBar);
      this.selectedRegions = _.cloneDeep(this.selectedRegionsInSideBar);
    }
    const includedSelectedRegions = this.treeService.getCheckedParents(
      this.continents
    );
    this.regionMap.regionValues = this.convertSelectedRegionsToRegionValues(
      includedSelectedRegions
    );
    this.regionMap.targetedRegions = this.convertSelectedRegionsToTargetedRegions(
      this.selectedRegions
    );
    this.geoTargetingControl.patchValue(this.regionMap);
    if (this.open) {
      this.open = false;
    }
  }

  get geoTargetingControl() {
    return this.formArray.controls[this.group].get("regionMap") as FormControl;
  }

  get targetedRegions() {
    return this.geoTargetingControl.get("targetedRegions") as FormControl;
  }

  get isDisabled(): boolean {
    return this.geoTargetingControl.disabled;
  }

}
